import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CalendarDays, Clock, MapPin, ExternalLink, Images, Mail, Truck } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { NotifySignup, NOTIFY_OPEN_EVENT } from "../components/NotifySignup";
import { SaleGallery, useSaleGalleryPhotos } from "../components/SaleGallery";
import { GalleryLightbox } from "../components/GalleryLightbox";
import { SALES } from "../data/sales";

const INTRO_PARAGRAPHS = [
  "We're back for one final weekend! MacWay Estate Services is returning to our beautiful Easton estate with incredible deals on the remaining furniture, carpets, area rugs, upholstered seating, tables, artwork, home décor, and more.",
  "We're priced to sell — deep discounts across every room, with multiple-item purchases encouraged and reasonable offers welcome.",
  "Buying furniture or carpets? Delivery is available for an additional fee — just ask our team.",
];

export default function UpcomingSalesPage() {
  const sale = SALES[0];
  usePageMeta(
    "Easton MD Flash Sale: Furniture Liquidation — November 7–8 Estate Sale | MacWay Estate Services",
    "Easton, Maryland flash sale, November 7–8, 2026: deep-discount furniture, carpets, area rugs, artwork, and home décor at our final-weekend furniture liquidation in Easton, Maryland. Delivery available for an additional fee. Preview the inventory gallery."
  );
  const photos = useSaleGalleryPhotos(sale.id);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const previewPhotos = photos ? photos.slice(0, 6) : [];
  const openNotify = () => window.dispatchEvent(new Event(NOTIFY_OPEN_EVENT));

  // Deep links like /upcoming-estate-sales#gallery must wait for the
  // asynchronously loaded gallery before scrolling to it.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    try {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } catch {
      /* invalid selector — ignore */
    }
  }, [hash, photos]);

  return (
    <main data-testid="upcoming-sales-page">
      {/* Title + introduction */}
      <section className="bg-ivory py-16 md:py-24" data-testid="upcoming-sales-intro">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow-label">Upcoming Estate Sales</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center bg-brass/15 border border-brass/50 text-brass-hover px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.16em]" data-testid="sale-status-badge">
              {sale.status}
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60">
              <CalendarDays className="w-4 h-4 text-brass" /> {sale.timeframe}
            </span>
          </div>
          <h1 className="mt-5 text-5xl sm:text-6xl font-semibold tracking-tight text-ink" data-testid="upcoming-sales-headline">
            Easton Flash Sale: Furniture &amp; Carpets Must Go!
          </h1>
          <p className="mt-4 font-serif text-2xl sm:text-3xl text-brass-hover italic" data-testid="upcoming-sales-subtitle">
            One Final Weekend — Deep Discounts, Everything Must Go
          </p>
          <div className="mt-7 space-y-5 max-w-3xl" data-testid="upcoming-sales-description">
            {INTRO_PARAGRAPHS.map((p, i) => (
              <p key={i} className="text-base sm:text-lg text-ink/70 leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a href="#collection" className="btn-brass" data-testid="cta-preview-collection">
              <Images className="w-4 h-4" /> View Flash Sale Inventory
            </a>
            <button type="button" onClick={openNotify} className="btn-outline-dark" data-testid="cta-sale-updates">
              <Mail className="w-4 h-4" /> Get Sale Updates
            </button>
          </div>
          {sale.estatesalesUrl && (
            <a
              href={sale.estatesalesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-forest hover:text-brass transition-colors"
              data-testid="estatesales-link-intro"
            >
              View the official listing on EstateSales.NET <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </section>

      {/* Featured estate photograph */}
      <section className="bg-sand py-14 md:py-20" data-testid="easton-featured">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-linen p-3 shadow-lg">
            <img
              src="/images/easton-featured.webp"
              alt="Antique upholstered seating, elegant lighting, fine furnishings, and richly patterned rugs offered at the Easton flash sale"
              className="w-full h-auto"
              data-testid="easton-featured-photo"
            />
          </div>
        </div>
      </section>

      {/* Featured merchandise preview (first photos from the admin-managed gallery) */}
      {previewPhotos.length > 0 && (
        <section id="collection" className="bg-white py-14 md:py-20" data-testid="collection-preview">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <p className="eyebrow-label">Featured merchandise</p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight text-ink" data-testid="collection-preview-heading">
                A Preview of the Collection
              </h2>
              <p className="mt-3 text-sm sm:text-base text-ink/70 leading-relaxed">
                A first look at remaining highlights — furniture, carpets, area rugs, upholstered seating, tables,
                artwork, lamps, and decorative pieces, all priced to sell.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3 items-start max-w-6xl mx-auto" data-testid="collection-preview-grid">
              {previewPhotos.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="block w-full border border-linen bg-white overflow-hidden group cursor-pointer"
                  data-testid={`collection-preview-thumb-${i}`}
                  aria-label={`Enlarge photo ${i + 1}${p.caption ? `: ${p.caption}` : ""}`}
                >
                  <img
                    src={p.image_url}
                    alt={p.caption || `Flash sale item photo ${i + 1}`}
                    loading="lazy"
                    className="w-full h-auto group-hover:opacity-90 transition-opacity"
                  />
                </button>
              ))}
            </div>
            <p className="mt-8 text-center text-sm text-ink/50">The complete photo gallery follows below.</p>
          </div>
        </section>
      )}

      {/* Sale information */}
      <section className="bg-forest py-14 md:py-20" data-testid="plan-your-visit">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="eyebrow-label">Sale information</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight text-ivory" data-testid="plan-your-visit-heading">
              Plan Your Visit
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="plan-your-visit-cards">
            <div className="border border-ivory/15 bg-white/5 p-6">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-brass flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Location
              </p>
              <p className="mt-3 text-ivory/90 text-lg" data-testid="visit-location">{sale.visit.location}</p>
              <p className="mt-2 text-sm text-ivory/60 leading-relaxed" data-testid="visit-policies">{sale.visit.policies}</p>
            </div>
            <div className="border border-ivory/15 bg-white/5 p-6">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-brass flex items-center gap-2">
                <Clock className="w-4 h-4" /> Dates
              </p>
              <p className="mt-3 text-ivory/90 text-lg" data-testid="visit-dates">{sale.visit.dates}</p>
              <p className="mt-2 text-sm text-ivory/60 leading-relaxed" data-testid="visit-hours-note">{sale.visit.hoursNote}</p>
            </div>
          </div>
          {sale.estatesalesUrl && (
            <div className="mt-8 text-center">
              <a
                href={sale.estatesalesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-brass"
                data-testid="estatesales-link-visit"
              >
                View Official Sale Details <ExternalLink className="w-4 h-4" />
              </a>
              <p className="mt-3 text-xs text-ivory/50">Hosted on EstateSales.NET — the official listing for this sale.</p>
            </div>
          )}
        </div>
      </section>

      {/* Delivery services */}
      <section className="bg-sand py-12 md:py-16 border-b border-linen" data-testid="delivery-band">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="eyebrow-label flex items-center justify-center gap-2">
            <Truck className="w-4 h-4" /> Delivery services
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight text-ink" data-testid="delivery-heading">
            Buying Furniture or Carpets? Delivery Is Available!
          </h2>
          <p className="mt-4 text-base sm:text-lg text-ink/70 leading-relaxed">
            Found something you love but don&rsquo;t have a truck? MacWay Estate Services offers delivery for an
            additional fee. Contact our team to discuss availability and pricing.
          </p>
          <Link to="/contact" className="btn-brass mt-7 inline-flex" data-testid="delivery-cta">
            Contact Us About Delivery
          </Link>
        </div>
      </section>

      {/* Complete photo gallery (admin-managed) */}
      <SaleGallery saleId={sale.id} photos={photos} onOpenPhoto={setLightboxIndex} />

      {/* Final invitation + email signup (existing) */}
      <NotifySignup sourcePage="upcoming-estate-sales" />

      {/* Estate-sale services cross-link (existing) */}
      <section className="py-14 md:py-16 bg-sand">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-ink">Need an estate sale of your own?</h2>
          <p className="mt-4 text-base text-ink/70 leading-relaxed">
            MacWay plans, stages, prices, and hosts estate sales across DC, Maryland, and Virginia.
          </p>
          <Link to="/services/estate-sales" className="btn-outline-dark mt-7" data-testid="upcoming-sales-service-link">
            Learn about our estate sale services
          </Link>
        </div>
      </section>

      <GalleryLightbox
        photos={photos || []}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />
    </main>
  );
}
